import React from "react";
import { useLocation, Navigate, Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { usePageMeta } from "@/lib/use-page-meta";
import { SEO_LANDING_PAGES } from "@/lib/seo-landing-data";
import { blogPosts } from "@/lib/blog-posts";
import { ArrowRight, Check, Sparkles, BookOpen } from "lucide-react";

// Per SEO landing page, link to 2 most-relevant blog posts. This creates the
// two-way internal-link graph Google uses to determine page authority.
const RELATED_BLOG_POSTS_FOR_GUIDE = {
  "google-analytics-for-beginners": [
    "how-to-connect-google-analytics-4-the-right-way",
    "how-to-read-ga4-reports-plain-english",
  ],
  "google-analytics-explained": [
    "ga4-metrics-that-actually-matter",
    "ga4-attribution-explained",
  ],
  "how-to-understand-google-analytics": [
    "five-minute-weekly-google-analytics-review",
    "how-to-read-ga4-reports-plain-english",
  ],
  "ga4-for-small-business-owners": [
    "ga4-for-small-business-owners-getting-started",
    "how-to-set-up-ga4-conversions-key-events",
  ],
  "what-do-google-analytics-numbers-mean": [
    "ga4-metrics-that-actually-matter",
    "ga4-numbers-dont-match-other-tools",
  ],
  "google-analytics-vs-ga4": [
    "whats-new-in-ga4-2026",
    "ga4-numbers-dont-match-other-tools",
  ],
};

const signIn = () => {
  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  window.location.href = `${apiUrl}/api/auth/google/start?next=/dashboard`;
};

export default function SeoLanding() {
  // Each landing page is wired as its own route (one per slug in App.js), so
  // we derive the slug from the URL pathname rather than route params.
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, "").replace(/\/$/, "");
  const data = SEO_LANDING_PAGES[slug];
  const path = `/${slug}`;

  // Hooks must run unconditionally — pass safe defaults if slug is unknown.
  usePageMeta({
    title: data?.title,
    description: data?.description,
    keywords: data?.keywords,
    // Each SEO landing page has its own custom 1200×630 OG image baked at
    // build time with the page's H1 — way better social-share CTR than the
    // generic site OG. Falls back to the default if a slug ever ships
    // without an image file.
    ogImage: slug ? `https://myplainly.com/brand/og/${slug}.png` : undefined,
    path,
  });

  // Unknown slug → fall back to landing so we don't return a 404 in-app
  // (Google won't crawl unknown SPA routes anyway).
  if (!data) return <Navigate to="/" replace />;

  // Article-style JSON-LD so Google can show this as rich content.
  // datePublished + dateModified are required for the Article rich-result and
  // power Google's freshness signal. Nicholas as Person/author boosts E-E-A-T.
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: data.h1,
    description: data.description,
    keywords: (data.keywords || []).join(", "),
    inLanguage: "en-US",
    datePublished: data.datePublished || "2026-02-01",
    dateModified: data.dateModified || new Date().toISOString().slice(0, 10),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://myplainly.com${path}`,
    },
    image: slug ? `https://myplainly.com/brand/og/${slug}.png` : undefined,
    author: {
      "@type": "Person",
      "@id": "https://myplainly.com/about#person",
      name: "Nicholas Steer",
      url: "https://myplainly.com/about",
    },
    publisher: {
      "@type": "Organization",
      "@id": "https://myplainly.com/#organization",
      name: "Plainly",
      logo: {
        "@type": "ImageObject",
        url: "https://myplainly.com/brand/plainly-mark-512.png",
      },
    },
  };

  const faqJsonLd = data.faq?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: data.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  // Speakable schema — for Google Assistant + voice search. Targets the H1
  // and the first paragraph (intro), which is the cleanest spoken answer.
  const speakableJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `https://myplainly.com${path}#webpage`,
    url: `https://myplainly.com${path}`,
    name: data.h1,
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", "[data-speakable]"],
    },
  };

  // BreadcrumbList — improves crawl-path signal and powers breadcrumb rich
  // results in Google. Already present for blog posts; now matches SEO pages.
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://myplainly.com/" },
      { "@type": "ListItem", position: 2, name: "Guides", item: "https://myplainly.com/blog" },
      { "@type": "ListItem", position: 3, name: data.h1, item: `https://myplainly.com${path}` },
    ],
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <SiteHeader />

      {/* HERO */}
      <section className="relative">
        <div className="max-w-4xl mx-auto px-6 md:px-8 pt-16 md:pt-20 pb-10">
          <div className="tag-label text-[#E27D60] mb-5">Plainly · Guide</div>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            data-testid="seo-landing-h1"
          >
            {data.h1}
          </h1>
          <p
            className="mt-6 text-lg text-[#1E392A]/75 max-w-2xl leading-relaxed"
            data-speakable="true"
          >
            {data.intro}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Button
              onClick={signIn}
              className="rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white h-11 px-6"
              data-testid="seo-landing-cta-top"
            >
              {data.cta} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <Link
              to="/#pricing"
              className="rounded-full text-sm h-11 px-5 inline-flex items-center border border-[#1E392A]/15 hover:bg-white"
            >
              See pricing
            </Link>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="bg-white border-t border-black/5">
        <div className="max-w-3xl mx-auto px-6 md:px-8 py-14 md:py-20">
          <article className="space-y-12">
            {data.sections.map((s, i) => (
              <div key={i}>
                <h2 className="font-display text-2xl sm:text-3xl mb-4 text-[#1E392A]">
                  {s.heading}
                </h2>
                {s.body?.map((p, j) => (
                  <p
                    key={`b-${j}`}
                    className="text-[#1E392A]/80 leading-relaxed mb-4"
                    dangerouslySetInnerHTML={{ __html: renderInline(p) }}
                  />
                ))}
                {s.bullets && (
                  <ul className="space-y-2 mt-3 mb-2">
                    {s.bullets.map((b, k) => (
                      <li
                        key={`l-${k}`}
                        className="flex gap-3 text-[#1E392A]/85"
                      >
                        <Check className="w-4 h-4 text-[#E27D60] mt-1 shrink-0" />
                        <span dangerouslySetInnerHTML={{ __html: renderInline(b) }} />
                      </li>
                    ))}
                  </ul>
                )}
                {s.bodyAfter?.map((p, j) => (
                  <p
                    key={`ba-${j}`}
                    className="text-[#1E392A]/80 leading-relaxed mt-4"
                    dangerouslySetInnerHTML={{ __html: renderInline(p) }}
                  />
                ))}
              </div>
            ))}
          </article>

          {/* Inline CTA */}
          <div className="mt-14 rounded-3xl bg-[#1E392A] text-[#F7F5F0] p-8 md:p-10 bento-shadow">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#E27D60] grid place-items-center shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-2xl sm:text-3xl">
                  Skip the manual checks.
                </h3>
                <p className="text-[#F7F5F0]/75 mt-2 max-w-xl">
                  Plainly connects to your Google Analytics in one click and emails you a 60-second plain-English readout every Monday. $18/month or $199/year for unlimited analyses.
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <Button
                    onClick={signIn}
                    className="rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white h-11 px-6"
                    data-testid="seo-landing-cta-mid"
                  >
                    Try Plainly free <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                  <Link
                    to="/blog"
                    className="rounded-full text-sm h-11 px-5 inline-flex items-center border border-[#F7F5F0]/25 hover:bg-white/10"
                  >
                    Read more guides
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ */}
          {data.faq?.length > 0 && (
            <div className="mt-14">
              <h2 className="font-display text-2xl sm:text-3xl mb-6">
                Common questions
              </h2>
              <div className="space-y-5">
                {data.faq.map((f, i) => (
                  <div key={i}>
                    <h3 className="font-display text-lg text-[#1E392A]">
                      {f.q}
                    </h3>
                    <p className="text-[#1E392A]/75 mt-1.5 leading-relaxed">
                      {f.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Related links — internal linking is a strong SEO signal */}
          <div className="mt-14 pt-10 border-t border-black/10">
            <h2 className="font-display text-xl mb-4">Keep reading</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(SEO_LANDING_PAGES)
                .filter(([s]) => s !== slug)
                .slice(0, 4)
                .map(([s, p]) => (
                  <Link
                    key={s}
                    to={`/${s}`}
                    className="rounded-xl border border-black/10 p-4 hover:border-[#E27D60]/40 hover:bg-[#F7F5F0]/40 transition-colors"
                  >
                    <div className="font-medium text-[#1E392A] text-sm">
                      {p.h1}
                    </div>
                    <div className="text-xs text-[#1E392A]/60 mt-1 line-clamp-2">
                      {p.description}
                    </div>
                  </Link>
                ))}
              <Link
                to="/blog"
                className="rounded-xl border border-black/10 p-4 hover:border-[#E27D60]/40 hover:bg-[#F7F5F0]/40 transition-colors"
              >
                <div className="font-medium text-[#1E392A] text-sm">
                  Plainly blog — weekly GA4 deep-dives
                </div>
                <div className="text-xs text-[#1E392A]/60 mt-1">
                  Long-form analysis pieces, case studies, and how-tos.
                </div>
              </Link>
            </div>
          </div>

          {/* Related deep-dive blog posts — two-way internal-link graph that
              signals to Google these guides and our blog are tightly linked. */}
          {(() => {
            const relatedSlugs = RELATED_BLOG_POSTS_FOR_GUIDE[slug] || [];
            const related = relatedSlugs
              .map((s) => blogPosts.find((p) => p.slug === s))
              .filter(Boolean);
            if (related.length === 0) return null;
            return (
              <div className="mt-10" data-testid="seo-landing-related-blog">
                <h2 className="font-display text-xl mb-4 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-[#E27D60]" />
                  Related deep-dives from the Plainly blog
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {related.map((p) => (
                    <Link
                      key={p.slug}
                      to={`/blog/${p.slug}`}
                      className="rounded-xl border border-black/10 p-4 hover:border-[#E27D60]/40 hover:bg-[#F7F5F0]/40 transition-colors group"
                      data-testid={`seo-landing-blog-link-${p.slug}`}
                    >
                      <div className="font-medium text-[#1E392A] text-sm group-hover:text-[#a8492a] transition-colors">
                        {p.title}
                      </div>
                      <div className="text-xs text-[#1E392A]/60 mt-1 line-clamp-2">
                        {p.description}
                      </div>
                      <div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#E27D60]">
                        Read post <ArrowRight className="w-3 h-3" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(speakableJsonLd) }}
      />
      {faqJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      )}
    </div>
  );
}

// Minimal inline markdown — **bold** only, escape everything else.
function renderInline(text) {
  if (!text) return "";
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
}
